/**
 * Centralized logging utility that respects environment
 * Only logs in development mode to prevent information leakage in production
 * 
 * Usage:
 *   import { logger } from '../utils/logger';
 *   logger.log('Debug message');
 *   logger.error('Error message');
 *   logger.warn('Warning message');
 */

// Vite uses import.meta.env.MODE instead of process.env.NODE_ENV
// MODE is 'development' in dev mode, 'production' in build mode
const isDevelopment = import.meta.env.MODE === 'development';

export const logger = {
  /**
   * Log debug information (only in development)
   * Use for general debugging and informational messages
   * 
   * @param {...any} args - Arguments to log (same as console.log)
   * 
   * @example
   * logger.log('User logged in:', user.email);
   * logger.log('[API] Requesting data:', { endpoint, params });
   */
  log: (...args) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },

  /**
   * Log warnings (only in development)
   * Use for non-critical issues that should be investigated
   * 
   * @param {...any} args - Arguments to log (same as console.warn)
   * 
   * @example
   * logger.warn('[API] Received unexpected data format');
   * logger.warn('Deprecated function called');
   */
  warn: (...args) => {
    if (isDevelopment) {
      console.warn(...args);
    }
  },

  /**
   * Log errors (only in development)
   * Use for error conditions that need attention
   * 
   * Note: In production, errors are silenced to prevent information leakage.
   * For production error tracking, consider integrating with a service like Sentry.
   * 
   * @param {...any} args - Arguments to log (same as console.error)
   * 
   * @example
   * logger.error('API call failed:', error);
   * logger.error('[Auth] Could not get token:', error.message);
   */
  error: (...args) => {
    if (isDevelopment) {
      console.error(...args);
    }
    // In production, you could send to error tracking service (Sentry, etc.)
    // For now, we'll silence them to prevent information leakage
    // TODO: Consider adding production error tracking service
  },

  /**
   * Log info messages (only in development)
   * Use for informational messages that are less verbose than debug logs
   * 
   * @param {...any} args - Arguments to log (same as console.info)
   * 
   * @example
   * logger.info('Application initialized');
   * logger.info('Data loaded successfully');
   */
  info: (...args) => {
    if (isDevelopment) {
      console.info(...args);
    }
  },

  /**
   * Log debug messages (only in development)
   * Use for very detailed debugging information
   * 
   * @param {...any} args - Arguments to log (same as console.debug)
   * 
   * @example
   * logger.debug('Component state:', state);
   * logger.debug('API response:', response);
   */
  debug: (...args) => {
    if (isDevelopment) {
      console.debug(...args);
    }
  },
};

/**
 * Convenience function to check if logging is enabled
 * Useful for conditional logic that only runs in development
 * 
 * @returns {boolean} - True if in development mode
 * 
 * @example
 * if (logger.isEnabled()) {
 *   // Development-only code
 * }
 */
logger.isEnabled = () => isDevelopment;


